import "./layout.module.css?__remix_sideEffect__";

import { useEffect, useMemo } from 'react';
import { getRepubMetaFromRepub } from '@cx-web/utils';
import { json, type LinksFunction, type LoaderFunctionArgs, type SerializeFrom } from '@remix-run/node';
import { Outlet, useLoaderData, useMatches } from '@remix-run/react';

import Footer from '~/apps/product-plus/components/BettingInsights/components/Footer';
import { Header } from '~/apps/product-plus/components/Header';
import { AuthModalProvider } from '~/apps/product-plus/providers/auth-modal-provider';
import { getRequestContext } from '~/apps/product-plus/utils/getRequestContext.server';
import { featureFlags, useFeatureFlag } from '~/providers/feature-flag-provider';
import { GeolocationProvider } from '~/providers/geolocation-context';
import { LayoutStoreProvider } from '~/providers/layout-store-provider';
import { NavigationProvider } from '~/providers/navigation-context';
import { UIConfigProvider } from '~/providers/ui-config-context';
import stylesheet from '~/tailwind.css';
import { type RequestContext } from '~/types/RequestContext';

import { useGZConsent } from './gz/hooks/useGZConsent';
import { useGZConsentGatedGTM } from './gz/hooks/useGZConsentGatedGTM';
import { useGZConsentGatedMetaPixel } from './gz/hooks/useGZConsentGatedMetaPixel';
import { useGZTabTracking } from './gz/hooks/useGZTabTracking';
import { initChildIframeContext } from './gz/scripts';
import type { loader as rootLoader } from './root-loader';

function GZIframeChildInit() {
  useGZTabTracking();
  const { consentGranted } = useGZConsent();

  const matches = useMatches();
  const rootMatch = matches.find((match) => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader> | undefined;

  useGZConsentGatedGTM({
    consentGranted,
    geolocationInfo: rootLoaderData?.geolocationInfo,
    gtmId: rootLoaderData?.gtmId,
    userGroup: rootLoaderData?.myocData?.userGroup,
    userId: rootLoaderData?.myocData?.myOCUser?.userID
  });

  useGZConsentGatedMetaPixel(consentGranted);

  useEffect(() => {
    initChildIframeContext();
    import('@iframe-resizer/child');
  }, []);

  return null;
}

export const links: LinksFunction = () => [
{
  rel: 'stylesheet',
  href: 'https://cdn.jsdelivr.net/npm/instantsearch.css@7/themes/satellite-min.css'
},
{ rel: 'stylesheet', href: stylesheet }];


export const loader = async ({ context, request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const repub = context.repub;

  const requestContext = await getRequestContext.call(context, repub, request);

  return json({
    repub,
    requestContext,
    url
  });
};

export default function OddscheckerPlus() {
  const matches = useMatches();
  const rootMatch = matches.find((match) => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader>;
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }
  const { repubIs } = getRepubMetaFromRepub(rootLoaderData.repub);

  const {
    ENV: { BETSLIP_SCRIPT_URL, OC_API_KEY, PROSPERSTACK_URL },
    baseUrl,
    currentPath,
    deviceId,
    deviceType,
    geolocationInfo,
    isAdmiralCookieBannerEnabled,
    myocData,
    resourcesUrl,
    socialLoginIds,
    url: currentUrl
  } = rootLoaderData;

  const { repub, requestContext } = useLoaderData<typeof loader>();
  const subscriptions = myocData?.subscriptions;

  const isTipstersEnabled = useFeatureFlag(featureFlags.SUBS__PLUS__TIPSTERS);

  const layoutStoreValue = useMemo(
    () => ({
      deviceType,
      footerData: {} as any,
      isAdmiralCookieBannerEnabled,
      navData: {
        firstLevelNav: [],
        mobileBottomNav: [],
        mobileHamburgerNav: [],
        secondLevelNav: [],
        thirdLevelNav: []
      } as any,
      repub,
      resourcesUrl
    }),
    [deviceType, isAdmiralCookieBannerEnabled, repub, resourcesUrl]
  );

  return (
    <AuthModalProvider>
      <GeolocationProvider state={geolocationInfo.subdivisionCode}>
        <NavigationProvider currentPath={currentPath}>
          <UIConfigProvider deviceType={deviceType}>
            <LayoutStoreProvider value={layoutStoreValue}>
              <Header
                BETSLIP_SCRIPT_URL={BETSLIP_SCRIPT_URL}
                apiKey={OC_API_KEY[repub]}
                baseUrl={baseUrl}
                countryCode={geolocationInfo.countryCode}
                currentUrl={currentUrl}
                deviceId={deviceId}
                facebookID={socialLoginIds?.facebookID ?? ''}
                googleID={socialLoginIds?.googleID ?? ''}
                isTipstersEnabled={isTipstersEnabled}
                prosperStackUrl={PROSPERSTACK_URL}
                repub={rootLoaderData.repub}
                requestContext={requestContext as RequestContext}
                resourcesUrl={resourcesUrl}
                subscriptions={subscriptions} />

              <Outlet />
              {!repubIs.GZ && <Footer resourcesUrl={resourcesUrl} />}
              {repubIs.GZ && <GZIframeChildInit />}
            </LayoutStoreProvider>
          </UIConfigProvider>
        </NavigationProvider>
      </GeolocationProvider>
    </AuthModalProvider>);

}